import React from 'react';
import styled from 'styled-components';
import ReportPercentage from '../ReportPercentage';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactStar from 'shared/design-system/components/TrueImpactStar';
import { TrueImpactIndicatorFilesRender } from 'shared/design-system/components/TrueImpactFileRender';
import SummaryIcon from 'npo/pages/ReportBuilder/Components/ImpactSummary/SummaryIcon';
import {
  IndicatorByService,
  IndicatorSectionKey,
  stageIdToString
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import {
  getTimePeriodText,
  IndicatorDetails,
  SectionIndicators
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { impactSummaryTableGridStyles } from './ReportImpactSummaryHeader';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { findVariance, numberWithCommas } from 'shared/utils/numberUtils';
import {
  getIndicatorDetailsMeasurementKey,
  getLegacyIndicatorDetailsMeasurementKey,
  IndicatorDetailsMeasurementType
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import { capitalizeOnlyFirstLetter } from 'shared/utils/stringUtils';
import { IndicatorById } from 'shared/apollo/pageHooks/useAllIndicators';
import { replaceMadLib } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/IndicatorCardDetails';
import { ReportPeriod } from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { MEASURE_IDS } from 'npo/pages/ReportBuilder/constants';

const replaceLegacyText = (text: string) => {
  const measurement = MEASURE_IDS[text as keyof typeof MEASURE_IDS];
  if (!measurement) return text;
  return measurement.replace(' [please describe in next step]', '');
};

const measurementTypeToStarCount = (
  measurementType: IndicatorDetailsMeasurementType | undefined
) => {
  const greenStars =
    measurementType === 'Direct Measurement'
      ? 5
      : measurementType === 'Estimate Based on Data'
      ? 3
      : 1;

  return {
    greenStars,
    grayStars: 5 - greenStars
  };
};

interface ReportImpactSummarySectionProps {
  reportId: number | string;
  sectionKey: IndicatorSectionKey;
  impactSummary: IndicatorByService;
  impactDetails: SectionIndicators;
  programName?: string;
  beneficiaryTerm?: string;
  intermediaries?: string;
  reportPeriod: ReportPeriod;
  serviceGoal?: string;
  serviceType?: string;
  isReportPassed: boolean;
  reachHumanIndicator: IndicatorDetails | undefined;
  learnIndicator: IndicatorDetails | undefined;
  actIndicator: IndicatorDetails | undefined;
  indicators: IndicatorById;
  isLegacy: boolean;
}

const StyledDiv = styled.div`
  border-top: 1px solid ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayLight};
  .impact-summary-row {
    ${impactSummaryTableGridStyles}
    padding: ${SPACERS.medium} 16px;
    .report-success-criteria {
      margin-top: ${SPACERS.small};
      margin-bottom: ${SPACERS.small};
    }
    .report-star-wrapper {
      display: flex;
      gap: ${SPACERS.extraTiny};
      margin-bottom: ${SPACERS.tiny};
    }
    .report-estimation {
      margin-bottom: ${SPACERS.tiny};
    }
    .report-file {
      margin-top: ${SPACERS.small};
    }
  }
`;

const ReportImpactSummarySection: React.FC<ReportImpactSummarySectionProps> = ({
  reportId,
  sectionKey,
  impactSummary,
  impactDetails,
  programName,
  beneficiaryTerm,
  intermediaries,
  reportPeriod,
  serviceGoal,
  isReportPassed,
  reachHumanIndicator,
  learnIndicator,
  actIndicator,
  indicators,
  isLegacy
}) => {
  const { getSourcesAndAssumptions } = useIndicator();
  if (impactDetails[sectionKey].length === 0) return null;
  const impactSummarySection = impactSummary[sectionKey];
  if (!impactSummarySection) {
    return null;
  }
  return (
    <StyledDiv>
      {impactDetails[sectionKey].map((x, i) => {
        const forecast = x.finalForecast || x.forecast;
        const currentIndicator = indicators[x.indicatorId];
        const y = impactSummarySection.find(z => z.id === x.indicatorId);
        const variation = findVariance(x.forecast, x.finalForecast);
        const legacyMeasurementType = getLegacyIndicatorDetailsMeasurementKey(
          x.measurement
        );
        const measurementType =
          getIndicatorDetailsMeasurementKey(sectionKey, x.measurement) ||
          legacyMeasurementType.measurement;
        const stars = measurementTypeToStarCount(measurementType);
        const text =
          y?.impactReceiptText || currentIndicator?.impactReceiptText;
        const madLib = text
          ? replaceMadLib(
              text,
              programName,
              beneficiaryTerm,
              intermediaries,
              isReportPassed,
              reportPeriod,
              serviceGoal,
              reachHumanIndicator,
              learnIndicator,
              actIndicator,
              x.forecast,
              x.finalForecast,
              y
            )
          : undefined;
        const description = getSourcesAndAssumptions(x);

        return (
          <div className="impact-summary-row" key={x.indicatorId}>
            <div>
              {i === 0 && (
                <SummaryIcon
                  size={40}
                  maxWidth={94}
                  sectionKey={sectionKey}
                  text={stageIdToString(sectionKey)}
                />
              )}
            </div>
            <div>
              <TrueImpactText variant="body-small-bold">
                {numberWithCommas(forecast) || 0}{' '}
                {isLegacy ? x.indicatorName : madLib || x.indicatorDefinition}
              </TrueImpactText>
              <TrueImpactText
                variant="body-small"
                className="report-success-criteria"
              >
                <strong>Success criteria:</strong>{' '}
                {y?.updatedText || x.indicatorDefinition}
              </TrueImpactText>
              {x.happensAfterProgramYear && (
                <TrueImpactText
                  variant="body-small"
                  className="report-success-criteria"
                >
                  <strong>Occurs within program year:</strong>{' '}
                  {x.happensAfterProgramYear}
                </TrueImpactText>
              )}
              {x.timePeriod && (
                <TrueImpactText
                  variant="body-small"
                  className="report-success-criteria"
                >
                  <strong>Time period:</strong>{' '}
                  {getTimePeriodText(
                    x.timePeriod,
                    reportPeriod.startDate,
                    reportPeriod.endDate
                  )}
                </TrueImpactText>
              )}
              <TrueImpactText variant="body-small">
                <strong>Sources and assumptions:</strong>{' '}
                {description.length === 0
                  ? 'Not provided'
                  : description || 'Not provided'}
              </TrueImpactText>
              <TrueImpactIndicatorFilesRender indicator={x} />
            </div>
            <div>
              <div className="report-star-wrapper">
                <TrueImpactStar
                  count={stars.greenStars}
                  className="report-green-star"
                />
                {stars.grayStars > 0 && (
                  <TrueImpactStar
                    count={stars.grayStars}
                    starColor="grayLightest"
                    backgroundColor="grayLight"
                    className="report-gray-star"
                  />
                )}
              </div>
              <TrueImpactText
                variant="body-small-bold"
                className="report-estimation"
              >
                {capitalizeOnlyFirstLetter(measurementType)}
              </TrueImpactText>
              {x.measurement && (
                <TrueImpactText variant="body-small">
                  {legacyMeasurementType.isLegacy
                    ? replaceLegacyText(x.measurement)
                    : x.measurement}
                </TrueImpactText>
              )}
            </div>
            <div>
              <ReportPercentage percentage={variation} />
            </div>
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default ReportImpactSummarySection;
